import { useCallback, useEffect, useRef, useState } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { getVoiceToken } from '@/facilities/modules/aiHotlineSettings';

export type BrowserPhoneStatus = 'off' | 'connecting' | 'ready' | 'error';

export interface BrowserCall {
  callSid: string;
  callerNumber: string;
  since: number;
}

export interface BrowserPhone {
  status: BrowserPhoneStatus;
  error: string;
  call: BrowserCall | null;
  muted: boolean;
  toggleMute: () => void;
  hangUp: () => void;
}

/** console + window.__tmsPhone: lets support see what Twilio told the page without a debugger */
const trace = (event: string, detail?: unknown) => {
  const w = window as unknown as { __tmsPhone?: { events: unknown[] } };
  w.__tmsPhone = w.__tmsPhone || { events: [] };
  w.__tmsPhone.events.push({ at: new Date().toISOString(), event, detail });
  console.info(`[browser-phone] ${event}`, detail ?? '');
};

const fetchToken = async () => {
  const res = await getVoiceToken();
  const token: string | undefined = res.data?.body?.token;
  if (!token) throw new Error('no voice token');
  return token;
};

/**
 * Registers this browser with Twilio as the hotline operator (Twilio Client) while `enabled`, and
 * auto-answers the call the bot hands over on "Take over". One identity for every operator page;
 * the bot dials `client:tms-operator`.
 */
export const useBrowserPhone = (enabled: boolean): BrowserPhone => {
  const [status, setStatus] = useState<BrowserPhoneStatus>('off');
  const [error, setError] = useState('');
  const [call, setCall] = useState<BrowserCall | null>(null);
  const [muted, setMuted] = useState(false);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('off');
      return undefined;
    }
    let cancelled = false;
    let device: Device | null = null;
    setStatus('connecting');
    setError('');
    (async () => {
      try {
        const token = await fetchToken();
        if (cancelled) return;
        device = new Device(token, {
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          logLevel: 'error',
          closeProtection: false,
        });
        deviceRef.current = device;
        device.on('registered', () => {
          trace('registered');
          if (!cancelled) setStatus('ready');
        });
        device.on('unregistered', () => {
          trace('unregistered');
          if (!cancelled) setStatus('connecting');
        });
        device.on('error', (e: { message?: string; code?: number }) => {
          trace('device error', { code: e?.code, message: e?.message });
          if (cancelled) return;
          setError(e?.message || 'Twilio device error');
          setStatus('error');
        });
        device.on('tokenWillExpire', async () => {
          try {
            device?.updateToken(await fetchToken());
          } catch {
            /* the next registration attempt surfaces the error */
          }
        });
        device.on('incoming', (incoming: Call) => {
          const params = incoming.customParameters;
          const next: BrowserCall = {
            callSid: params.get('callSid') || '',
            callerNumber: params.get('callerNumber') || incoming.parameters.From || '',
            since: Date.now(),
          };
          trace('incoming', next);
          callRef.current = incoming;
          setMuted(false);
          const clear = () => {
            if (callRef.current === incoming) {
              callRef.current = null;
              setCall(null);
            }
          };
          incoming.on('accept', () => {
            trace('accepted');
            setCall({ ...next, since: Date.now() });
          });
          incoming.on('disconnect', () => {
            trace('disconnected');
            clear();
          });
          incoming.on('cancel', () => {
            trace('cancelled by the far end');
            clear();
          });
          incoming.on('reject', clear);
          incoming.on('error', (e: { message?: string; code?: number }) => {
            trace('call error', { code: e?.code, message: e?.message });
            clear();
          });
          // the operator asked for this call: answer straight away
          incoming.accept();
        });
        await device.register();
        trace('register requested');
      } catch (e: unknown) {
        trace('setup failed', String(e));
        if (cancelled) return;
        setError(
          String(
            (e as { detail?: string; message?: string })?.detail ||
              (e as Error)?.message ||
              e,
          ),
        );
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      callRef.current?.disconnect();
      callRef.current = null;
      setCall(null);
      device?.destroy();
      deviceRef.current = null;
    };
  }, [enabled]);

  const toggleMute = useCallback(() => {
    const active = callRef.current;
    if (!active) return;
    const next = !active.isMuted();
    active.mute(next);
    setMuted(next);
  }, []);

  const hangUp = useCallback(() => {
    callRef.current?.disconnect();
  }, []);

  return { status, error, call, muted, toggleMute, hangUp };
};
